import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Shield, ShieldCheck,
  Trash2, Search, ChevronDown, ChevronUp, Database, HardDrive, Clock,
  FileImage, Film, File, Image, Package, Archive, Eye, ArchiveRestore,
  Inbox, ClipboardCopy, Download, Layers, Tag, Zap, Monitor, Globe
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────── Category helpers ─────────────────────── */

const CATEGORY_COLORS = {
  image_export: { bg: '#0a0f1a', border: '#1a2a4a', text: '#60a5fa', icon: FileImage },
  video_export: { bg: '#0f0a1a', border: '#2a1a4a', text: '#a78bfa', icon: Film },
  bg_preset: { bg: '#0a140a', border: '#1a3a1a', text: '#4ade80', icon: Image },
  bg_custom: { bg: '#0a140f', border: '#1a3a2a', text: '#34d399', icon: Image },
  video_upload: { bg: '#140a0f', border: '#3a1a2a', text: '#f472b6', icon: Film },
  bg_upload: { bg: '#0f140a', border: '#2a3a1a', text: '#a3e635', icon: Image },
  video_proxy: { bg: '#0a0f14', border: '#1a2a3a', text: '#38bdf8', icon: Film },
  migrated_legacy: { bg: '#14140a', border: '#3a3a1a', text: '#fbbf24', icon: Archive },
  backup: { bg: '#0f0f14', border: '#1a1a3a', text: '#818cf8', icon: Package },
  test: { bg: '#14100a', border: '#3a2a1a', text: '#fb923c', icon: File },
  unknown: { bg: '#0f0f0f', border: '#1a1a1a', text: '#737373', icon: File },
};

function CategoryBadge({ category, label }) {
  const cat = CATEGORY_COLORS[category] || CATEGORY_COLORS.unknown;
  const Icon = cat.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 600,
      backgroundColor: cat.bg, border: `1px solid ${cat.border}`, color: cat.text,
      letterSpacing: '0.02em', textTransform: 'uppercase',
    }}>
      <Icon size={10} />
      {label || category}
    </span>
  );
}

/* ─────────────────────── Dev/Prod helpers ─────────────────────── */

function isDevKey(key) {
  if (!key) return false;
  return key.includes('qooqoo-dev') || key.includes('-dev/');
}

function EnvBadge({ s3Key }) {
  if (!s3Key) return null;
  const isDev = isDevKey(s3Key);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 6px', borderRadius: 3, fontSize: 8, fontWeight: 700,
      backgroundColor: isDev ? '#0a1414' : '#14100a',
      border: `1px solid ${isDev ? '#1a3a3a' : '#3a2a1a'}`,
      color: isDev ? '#2dd4bf' : '#f59e0b',
      letterSpacing: '0.06em', textTransform: 'uppercase',
      flexShrink: 0,
    }}>
      {isDev ? <Monitor size={8} /> : <Globe size={8} />}
      {isDev ? 'DEV' : 'PROD'}
    </span>
  );
}

function EnvFilterBar({ envFilter, setEnvFilter, report }) {
  const options = [
    { value: 'all', label: 'All', color: '#a3a3a3' },
    { value: 'prod', label: 'Prod', color: '#f59e0b', icon: Globe },
    { value: 'dev', label: 'Dev', color: '#2dd4bf', icon: Monitor },
  ];

  // Compute per-env orphan counts from report
  const orphans = report?.s3_orphans || [];
  const prodOrphans = orphans.filter(o => !isDevKey(o.key)).length;
  const devOrphans = orphans.filter(o => isDevKey(o.key)).length;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
      padding: '8px 12px', borderRadius: 8,
      backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#525252', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginRight: 4 }}>
        Environment
      </span>
      {options.map(opt => {
        const isActive = envFilter === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            onClick={() => setEnvFilter(opt.value)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 5, border: 'none',
              fontSize: 11, fontWeight: isActive ? 700 : 500, cursor: 'pointer',
              backgroundColor: isActive ? `${opt.color}18` : 'transparent',
              color: isActive ? opt.color : '#525252',
              outline: isActive ? `1px solid ${opt.color}40` : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {Icon && <Icon size={10} />}
            {opt.label}
          </button>
        );
      })}
      {report && (
        <span style={{ fontSize: 10, color: '#3a3a3a', marginLeft: 'auto', fontFamily: '"IBM Plex Mono", monospace' }}>
          {prodOrphans > 0 && <span style={{ color: '#f59e0b' }}>{prodOrphans} prod</span>}
          {prodOrphans > 0 && devOrphans > 0 && <span> · </span>}
          {devOrphans > 0 && <span style={{ color: '#2dd4bf' }}>{devOrphans} dev</span>}
          {prodOrphans === 0 && devOrphans === 0 && <span>0 orphans</span>}
        </span>
      )}
    </div>
  );
}

function ProdWarningModal({ action, prodCount, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        padding: 28, borderRadius: 12, maxWidth: 420, width: '90%',
        backgroundColor: '#0f0f0f', border: '2px solid #f59e0b',
        boxShadow: '0 0 40px rgba(245, 158, 11, 0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <AlertTriangle size={22} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>
            Production Files Included
          </h3>
        </div>
        <p style={{ fontSize: 13, color: '#d4d4d4', lineHeight: 1.6, margin: '0 0 8px' }}>
          This {action} includes <strong style={{ color: '#f59e0b' }}>{prodCount} production file{prodCount !== 1 ? 's' : ''}</strong>.
          Production files may be referenced by active carousels or users.
        </p>
        <p style={{ fontSize: 12, color: '#737373', lineHeight: 1.5, margin: '0 0 20px' }}>
          Are you sure you want to proceed?
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 18px', borderRadius: 6, border: '1px solid #2a2a2a',
              backgroundColor: '#1a1a1a', color: '#a3a3a3', fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 18px', borderRadius: 6, border: '1px solid #f59e0b',
              backgroundColor: '#f59e0b20', color: '#f59e0b', fontSize: 12,
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            Yes, proceed with {action}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── File Preview Thumbnail ─────────────────────── */

const IMAGE_EXTS = /\.(png|jpe?g|webp|gif)$/i;
const VIDEO_EXTS = /\.(mp4|mov|avi|mkv|webm)$/i;

function FilePreview({ s3Key, fetchKey, headers, size = 80 }) {
  // s3Key: used for extension detection and category display
  // fetchKey: the actual S3 key to use for presigned URL (defaults to s3Key)
  //   For quarantined files, s3Key is the original key (for display/ext detection)
  //   but fetchKey is the quarantine key (where the file actually lives in S3).
  const actualKey = fetchKey || s3Key;
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isImage = IMAGE_EXTS.test(s3Key || '');
  const isVideo = VIDEO_EXTS.test(s3Key || '');

  useEffect(() => {
    if (!isImage || !actualKey) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/admin/storage/preview?key=${encodeURIComponent(actualKey)}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (!cancelled) { setUrl(data.url); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [actualKey, isImage, headers]);

  const containerStyle = {
    width: size, height: size, borderRadius: 6, overflow: 'hidden',
    backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };

  if (isImage && url && !error) {
    return (
      <div style={containerStyle}>
        <img
          src={url} alt="" loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setError(true)}
        />
      </div>
    );
  }

  if (isImage && loading) {
    return (
      <div style={{ ...containerStyle, color: '#3a3a3a', fontSize: 9 }}>...</div>
    );
  }

  if (isVideo) {
    return (
      <div style={{
        ...containerStyle,
        background: 'linear-gradient(135deg, #1a0a2a 0%, #0a0a1a 100%)',
        border: '1px solid #2a1a4a',
      }}>
        <Film size={size * 0.35} color="#a78bfa" style={{ opacity: 0.7 }} />
      </div>
    );
  }

  // Non-previewable file
  const cat = CATEGORY_COLORS[_guessCategory(s3Key)] || CATEGORY_COLORS.unknown;
  const CatIcon = cat.icon;
  return (
    <div style={{ ...containerStyle, border: `1px solid ${cat.border}` }}>
      <CatIcon size={size * 0.3} color={cat.text} style={{ opacity: 0.5 }} />
    </div>
  );
}

function _guessCategory(key) {
  if (!key) return 'unknown';
  if (/exports?\//.test(key) && /\.png$/i.test(key)) return 'image_export';
  if (/exports?\//.test(key) && /\.mp4$/i.test(key)) return 'video_export';
  if (/presets?\//.test(key)) return 'bg_preset';
  if (/backgrounds?\//.test(key)) return 'bg_custom';
  if (/videos?\//.test(key) && /proxy/i.test(key)) return 'video_proxy';
  if (/videos?\//.test(key)) return 'video_upload';
  if (/migrated/.test(key)) return 'migrated_legacy';
  if (/backup/.test(key)) return 'backup';
  if (/test/.test(key)) return 'test';
  return 'unknown';
}

/* ─────────────────────── File Detail Panel (reusable expanded view) ─────────────────────── */

function FileDetailPanel({ s3Key, originalKey, size, sizeHuman, lastModified, ageHours, ageDays, category, categoryLabel, headers, children }) {
  const displayKey = originalKey || s3Key;
  // For quarantined files: s3Key is the quarantine key (actual S3 location),
  // originalKey is the pre-quarantine path (for display). Preview must fetch
  // from the actual location, but use the display key for extension detection.
  return (
    <div style={{ display: 'flex', gap: 14, padding: '12px 14px 14px' }}>
      {/* Preview thumbnail — fetchKey=s3Key for actual S3 location */}
      <FilePreview s3Key={displayKey} fetchKey={s3Key} headers={headers} size={72} />

      {/* Metadata */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <EnvBadge s3Key={displayKey} />
          <CategoryBadge category={category} label={categoryLabel} />
        </div>

        <div style={{ fontSize: 10, color: '#525252', lineHeight: 1.8, fontFamily: '"IBM Plex Mono", monospace' }}>
          {originalKey && (
            <div>
              <span style={{ color: '#3a3a3a' }}>Original Key:</span>{' '}
              <span style={{ color: '#737373', fontSize: 9, wordBreak: 'break-all' }}>{originalKey}</span>
            </div>
          )}
          <div>
            <span style={{ color: '#3a3a3a' }}>{originalKey ? 'Quarantine Key:' : 'Key:'}</span>{' '}
            <span style={{ color: originalKey ? '#3a3a3a' : '#737373', fontSize: 9, wordBreak: 'break-all' }}>{s3Key}</span>
          </div>
          <div>
            <span style={{ color: '#3a3a3a' }}>Size:</span>{' '}
            <span style={{ color: '#a0a0a0' }}>{sizeHuman} ({(size || 0).toLocaleString()} bytes)</span>
          </div>
          {lastModified && (
            <div>
              <span style={{ color: '#3a3a3a' }}>Last modified:</span>{' '}
              <span style={{ color: '#a0a0a0' }}>{new Date(lastModified).toLocaleString()}</span>
            </div>
          )}
          {(ageHours != null || ageDays != null) && (
            <div>
              <span style={{ color: '#3a3a3a' }}>Age:</span>{' '}
              <span style={{ color: ageHours != null && ageHours < 24 ? '#f87171' : '#a0a0a0' }}>
                {ageDays != null ? `${ageDays}d ` : ''}{ageHours != null ? `${Math.round(ageHours % 24)}h` : '—'}
              </span>
            </div>
          )}
        </div>

        {/* Extra children (validation layers, unsafe reasons, etc.) */}
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────── Validation Layer Indicators ─────────────────────── */

function LayerCheck({ passed, label, detail }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '2px 0', fontSize: 10,
    }}>
      {passed ? (
        <CheckCircle size={12} color="#4ade80" />
      ) : (
        <XCircle size={12} color="#f87171" />
      )}
      <span style={{ color: passed ? '#4ade80' : '#f87171', fontWeight: 600 }}>{label}</span>
      {detail && <span style={{ color: '#3a3a3a', marginLeft: 4 }}>{detail}</span>}
    </div>
  );
}

function ValidationLayers({ layers, orphan }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <LayerCheck passed={layers.no_db_record} label="L1: No DB record" />
      <LayerCheck
        passed={layers.no_carousel_ref}
        label="L2: No carousel refs"
        detail={!layers.no_carousel_ref ? `${orphan.carousel_refs?.length || 0} found` : null}
      />
      <LayerCheck
        passed={layers.no_render_job_ref}
        label="L3: No render job refs"
        detail={!layers.no_render_job_ref ? `job: ${orphan.render_job_ref?.job_id?.slice(0, 12)}` : null}
      />
      <LayerCheck
        passed={layers.not_recent}
        label="L5: Not recent (<24h)"
        detail={!layers.not_recent ? `${orphan.age_hours?.toFixed(0)}h old` : null}
      />
      {layers.category_safe !== undefined && (
        <LayerCheck
          passed={layers.category_safe}
          label="L4: Category safe"
          detail={!layers.category_safe ? `${orphan.category} — never auto-deletable` : null}
        />
      )}
      {layers.filename_not_in_db !== undefined && (
        <LayerCheck
          passed={layers.filename_not_in_db}
          label="L6: Filename unique"
          detail={!layers.filename_not_in_db ? 'Matches a DB record filename' : null}
        />
      )}
      {layers.no_asset_ref !== undefined && (
        <LayerCheck
          passed={layers.no_asset_ref}
          label="L7: No asset refs"
          detail={!layers.no_asset_ref ? 'In asset_references table' : null}
        />
      )}
      {layers.no_reverse_chain !== undefined && (
        <LayerCheck
          passed={layers.no_reverse_chain}
          label="L8: No reverse chain"
          detail={!layers.no_reverse_chain ? 'Key contains carousel-referenced ID' : null}
        />
      )}
    </div>
  );
}

/* ─────────────────────── Summary Card ─────────────────────── */

function SummaryCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 8,
      backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
      textAlign: 'center', minWidth: 0,
    }}>
      {Icon && <Icon size={14} color={color || '#525252'} style={{ marginBottom: 4 }} />}
      <div style={{
        fontSize: 24, fontWeight: 700, color: color || '#e5e5e5',
        fontFamily: '"IBM Plex Mono", monospace',
      }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 10, color: '#525252', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: '#3a3a3a', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

/* ─────────────────────── Tracking Breakdown (Option C) ─────────────────────── */

const TRACKING_SOURCES = [
  { key: 'user_assets',    label: 'User Assets',    desc: 'Backgrounds, uploads',    color: '#4ade80', icon: Image },
  { key: 'export_outputs', label: 'Export Outputs',  desc: 'Rendered images/videos',  color: '#60a5fa', icon: FileImage },
  { key: 'video_proxies',  label: 'Video Proxies',   desc: '1:1 with source video',   color: '#38bdf8', icon: Film },
  { key: 'system_backups', label: 'Backups',          desc: 'Migration backups',       color: '#818cf8', icon: Package },
];

function TrackingBreakdown({ breakdown }) {
  if (!breakdown) return null;
  const hasAny = TRACKING_SOURCES.some(s => (breakdown[s.key]?.count || 0) > 0);
  if (!hasAny) return null;

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 8,
      backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
      marginBottom: 16,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#525252', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 10,
      }}>
        Tracking Sources (Option C)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        {TRACKING_SOURCES.map(src => {
          const data = breakdown[src.key] || { count: 0, size_human: '0 B' };
          const Icon = src.icon;
          return (
            <div key={src.key} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 6,
              backgroundColor: '#050505', border: '1px solid #151515',
            }}>
              <Icon size={14} color={src.color} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: src.color, fontFamily: '"IBM Plex Mono", monospace' }}>
                  {data.count}
                </div>
                <div style={{ fontSize: 9, color: '#525252' }}>{src.label}</div>
                <div style={{ fontSize: 8, color: '#3a3a3a' }}>{data.size_human}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── Agent Debug Log Button ─────────────────────── */

function AgentLogButton({ headers }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const exportLog = async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const r = await fetch(`${API}/api/admin/storage/agent-log`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const text = JSON.stringify(data, null, 2);

      // Try clipboard first, fall back to download
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch {
        // Clipboard failed (insecure context) — download as file
        const blob = new Blob([text], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `agent-debug-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={exportLog}
        disabled={loading}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '7px 12px', borderRadius: 6,
          border: '1px solid #2a1a3a',
          backgroundColor: copied ? '#0a1a0a' : '#0a0a14',
          color: copied ? '#4ade80' : '#818cf8',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
          opacity: loading ? 0.5 : 1,
        }}
      >
        {copied ? <CheckCircle size={12} /> : loading ? <RefreshCw size={12} /> : <ClipboardCopy size={12} />}
        {loading ? 'Generating...' : copied ? 'Copied!' : 'Agent Log'}
      </button>
      {error && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          padding: '4px 8px', borderRadius: 4, fontSize: 9,
          backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a', color: '#f87171',
          whiteSpace: 'nowrap',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Typed Confirmation Modal ─────────────────────── */

/* ─────────────────────── Confirm Modal (reusable) ─────────────────────── */

function ConfirmModal({ title, description, confirmLabel, confirmColor, requireTyped, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  const isTypedOk = !requireTyped || typed === requireTyped;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }}>
      <div style={{
        width: 440, padding: 28, borderRadius: 12,
        backgroundColor: '#0f0f0f', border: `1px solid ${confirmColor === '#f87171' ? '#3a1a1a' : '#2a2a1a'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <AlertTriangle size={20} color={confirmColor || '#fbbf24'} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: confirmColor || '#fbbf24' }}>
            {title}
          </h3>
        </div>

        <div style={{ fontSize: 12, color: '#a0a0a0', lineHeight: 1.6, marginBottom: 16 }}>
          {description}
        </div>

        {requireTyped && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 6 }}>
              Type <strong style={{ color: confirmColor || '#f87171', fontFamily: '"IBM Plex Mono", monospace' }}>{requireTyped}</strong> to confirm:
            </label>
            <input
              ref={inputRef}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={requireTyped}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 6,
                border: `1px solid ${confirmColor === '#f87171' ? '#2a1a1a' : '#2a2a1a'}`,
                backgroundColor: '#0a0a0a',
                color: confirmColor || '#f87171', fontSize: 14,
                fontFamily: '"IBM Plex Mono", monospace',
                fontWeight: 700, outline: 'none', boxSizing: 'border-box',
                letterSpacing: '0.1em',
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={s.cancelBtn}>Cancel</button>
          <button
            onClick={() => onConfirm()}
            disabled={!isTypedOk}
            style={{
              ...(confirmColor === '#f87171' ? s.dangerBtn : s.quarantineBtn),
              opacity: isTypedOk ? 1 : 0.3,
              cursor: isTypedOk ? 'pointer' : 'not-allowed',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Category Breakdown ─────────────────────── */

function CategoryBreakdown({ breakdown }) {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const entries = Object.entries(breakdown).sort((a, b) => b[1].size - a[1].size);

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#737373' }}>
        By Category
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
        {entries.map(([cat, data]) => {
          const catStyle = CATEGORY_COLORS[cat] || CATEGORY_COLORS.unknown;
          const Icon = catStyle.icon;
          return (
            <div key={cat} style={{
              padding: '10px 12px', borderRadius: 6,
              backgroundColor: catStyle.bg, border: `1px solid ${catStyle.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Icon size={12} color={catStyle.text} />
                <span style={{ fontSize: 10, color: catStyle.text, fontWeight: 600, textTransform: 'uppercase' }}>
                  {cat.replace(/_/g, ' ')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                <span style={{ color: '#a0a0a0' }}>{data.count} files</span>
                <span style={{ color: '#737373' }}>{data.size_human}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 10, marginTop: 2 }}>
                <span style={{ color: '#4ade80' }}>✓ {data.safe}</span>
                {data.unsafe > 0 && <span style={{ color: '#f87171' }}>✗ {data.unsafe}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── Orphan Table ─────────────────────── */

function OrphanTable({ orphans, selectedKeys, onToggleKey, onToggleAll, headers }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const safeOrphans = orphans.filter(o => o.safe_to_delete);
  const unsafeOrphans = orphans.filter(o => !o.safe_to_delete);
  const allSafeSelected = safeOrphans.length > 0 && safeOrphans.every(o => selectedKeys.has(o.key));

  const renderRow = (orphan, i) => {
    const isExpanded = expandedRow === orphan.key;
    return (
      <React.Fragment key={orphan.key}>
        <tr style={{
          borderTop: '1px solid #1a1a1a',
          backgroundColor: !orphan.safe_to_delete ? '#0f0808' : selectedKeys.has(orphan.key) ? '#080f08' : 'transparent',
        }}>
          <td style={{ padding: '6px 10px', width: 32 }}>
            {orphan.safe_to_delete ? (
              <input
                type="checkbox"
                checked={selectedKeys.has(orphan.key)}
                onChange={() => onToggleKey(orphan.key)}
                style={{ cursor: 'pointer', accentColor: '#4ade80' }}
              />
            ) : (
              <Shield size={12} color="#f87171" style={{ opacity: 0.5 }} />
            )}
          </td>
          <td style={{ padding: '6px 10px' }}>
            {orphan.safe_to_delete ? (
              <ShieldCheck size={14} color="#4ade80" />
            ) : (
              <AlertTriangle size={14} color="#f87171" />
            )}
          </td>
          <td style={{ padding: '6px 10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <EnvBadge s3Key={orphan.key} />
              <CategoryBadge category={orphan.category} label={orphan.category_label} />
            </div>
          </td>
          <td style={{
            padding: '6px 10px', color: '#a0a0a0', fontSize: 11,
            maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: '"IBM Plex Mono", monospace',
          }} title={orphan.key}>
            {orphan.filename}
          </td>
          <td style={{ padding: '6px 10px', color: '#737373', fontSize: 11, textAlign: 'right' }}>
            {orphan.size_human}
          </td>
          <td style={{ padding: '6px 10px', color: '#525252', fontSize: 10, textAlign: 'right' }}>
            {orphan.age_days !== null ? (
              orphan.age_days === 0 ? `${Math.round(orphan.age_hours)}h` : `${orphan.age_days}d`
            ) : '—'}
          </td>
          <td style={{ padding: '6px 10px', width: 40, textAlign: 'center' }}>
            <button
              onClick={() => setExpandedRow(isExpanded ? null : orphan.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                color: isExpanded ? '#4ade80' : '#3a3a3a',
              }}
            >
              {isExpanded ? <ChevronUp size={14} /> : <Eye size={14} />}
            </button>
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan={7} style={{
              padding: 0,
              backgroundColor: '#080808', borderTop: 'none',
            }}>
              <FileDetailPanel
                s3Key={orphan.key}
                size={orphan.size}
                sizeHuman={orphan.size_human}
                lastModified={orphan.last_modified}
                ageHours={orphan.age_hours}
                ageDays={orphan.age_days}
                category={orphan.category}
                categoryLabel={orphan.category_label}
                headers={headers}
              >
                {/* Validation layers + orphan-specific extras */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#525252', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Validation Layers
                    </div>
                    <ValidationLayers layers={orphan.layers} orphan={orphan} />
                  </div>
                  <div>
                    {orphan.unsafe_reasons?.length > 0 && (
                      <div style={{ padding: '6px 10px', borderRadius: 4, backgroundColor: '#1a0808', border: '1px solid #3a1a1a' }}>
                        <div style={{ color: '#f87171', fontWeight: 600, fontSize: 10, marginBottom: 3 }}>⚠ Why Unsafe:</div>
                        {orphan.unsafe_reasons.map((reason, ri) => (
                          <div key={ri} style={{ color: '#c08080', fontSize: 9, paddingLeft: 8, lineHeight: 1.5 }}>
                            • {reason}
                          </div>
                        ))}
                      </div>
                    )}
                    {orphan.carousel_refs?.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>⚠ Referenced in carousels:</span>
                        {orphan.carousel_refs.map((ref, ri) => (
                          <div key={ri} style={{ paddingLeft: 12, color: '#a08080', fontSize: 9 }}>
                            {ref.carousel_name || ref.carousel_id?.slice(0, 12)} slide #{ref.slide_index}
                            {ref.matched_fragment && <span style={{ color: '#5a3a3a' }}> (via "{ref.matched_fragment}")</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {orphan.render_job_ref && (
                      <div style={{ marginTop: 4, fontSize: 10 }}>
                        <span style={{ color: '#f87171', fontWeight: 600 }}>⚠ Render job:</span>{' '}
                        <span style={{ color: '#a08080', fontSize: 9 }}>
                          {orphan.render_job_ref.job_id} ({orphan.render_job_ref.kind}, {orphan.render_job_ref.status})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </FileDetailPanel>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      {/* Unsafe orphans first */}
      {unsafeOrphans.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: '8px 8px 0 0',
            backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a',
          }}>
            <AlertTriangle size={14} color="#f87171" />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#f87171' }}>
              {unsafeOrphans.length} Unsafe — Referenced or Recent
            </span>
            <span style={{ fontSize: 10, color: '#5a2a2a', marginLeft: 4 }}>
              (cannot be deleted until references are resolved)
            </span>
          </div>
          <div style={{
            maxHeight: 300, overflowY: 'auto',
            backgroundColor: '#0a0808', border: '1px solid #3a1a1a', borderTop: 'none',
            borderRadius: '0 0 8px 8px',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>{unsafeOrphans.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Safe orphans */}
      {safeOrphans.length > 0 && (
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px', borderRadius: '8px 8px 0 0',
            backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={14} color="#4ade80" />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#4ade80' }}>
                {safeOrphans.length} Safe to Delete
              </span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#3a5a3a', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={allSafeSelected}
                onChange={() => onToggleAll(safeOrphans.map(o => o.key))}
                style={{ accentColor: '#4ade80', cursor: 'pointer' }}
              />
              Select all safe
            </label>
          </div>
          <div style={{
            maxHeight: 400, overflowY: 'auto',
            backgroundColor: '#080f08', border: '1px solid #1a3a1a', borderTop: 'none',
            borderRadius: '0 0 8px 8px',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: '#3a5a3a', textAlign: 'left', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '6px 10px', width: 32 }}></th>
                  <th style={{ padding: '6px 10px', width: 28 }}></th>
                  <th style={{ padding: '6px 10px' }}>Category</th>
                  <th style={{ padding: '6px 10px' }}>Filename</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right' }}>Size</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right' }}>Age</th>
                  <th style={{ padding: '6px 10px', width: 40 }}></th>
                </tr>
              </thead>
              <tbody>{safeOrphans.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── DB Orphans Section ─────────────────────── */

function DbOrphansSection({ report }) {
  const [expanded, setExpanded] = useState(false);
  if (!report || report.db_orphans_total === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', borderRadius: expanded ? '8px 8px 0 0' : 8,
        backgroundColor: '#0f0a0a', border: '1px solid #2a1a1a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Database size={14} color="#f97316" />
          <span style={{ color: '#f97316', fontSize: 12, fontWeight: 600 }}>
            {report.db_orphans_total} DB orphan{report.db_orphans_total !== 1 ? 's' : ''} (no S3 object)
          </span>
        </div>
        <button onClick={() => setExpanded(!expanded)} style={{ ...s.toggleBtn, borderColor: '#2a1a1a', color: '#f97316' }}>
          {expanded ? '▼ Hide' : '▶ Show'}
        </button>
      </div>
      {expanded && (
        <div style={{
          maxHeight: 300, overflowY: 'auto', padding: '8px 0',
          backgroundColor: '#0a0808', border: '1px solid #2a1a1a', borderTop: 'none',
          borderRadius: '0 0 8px 8px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ color: '#5a3a3a', textAlign: 'left' }}>
                <th style={{ padding: '4px 10px' }}>Asset ID</th>
                <th style={{ padding: '4px 10px' }}>Filename</th>
                <th style={{ padding: '4px 10px' }}>Storage Key</th>
                <th style={{ padding: '4px 10px' }}>State</th>
              </tr>
            </thead>
            <tbody>
              {report.db_orphans.map((o, i) => (
                <tr key={i} style={{ borderTop: '1px solid #1a0a0a' }}>
                  <td style={{ padding: '4px 10px', color: '#525252', fontFamily: '"IBM Plex Mono", monospace', fontSize: 9 }}>{o.asset_id?.slice(0, 16)}...</td>
                  <td style={{ padding: '4px 10px', color: '#a08080' }}>{o.filename}</td>
                  <td style={{ padding: '4px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <EnvBadge s3Key={o.storage_key} />
                      <span style={{ color: '#525252', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', fontFamily: '"IBM Plex Mono", monospace', fontSize: 9 }}>{o.storage_key}</span>
                    </div>
                  </td>
                  <td style={{ padding: '4px 10px', color: '#525252' }}>{o.migration_state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Carousel Issues Section ─────────────────────── */

function CarouselIssuesSection({ report, headers, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  if (!report || report.carousel_issues_total === 0) return null;

  const runAutoRepair = async () => {
    setRepairing(true);
    try {
      const r = await fetch(`${API}/api/admin/storage/reconcile/auto-repair`, { method: 'POST', headers });
      setRepairResult(await r.json());
      // Refresh reconciliation to update summary cards
      setTimeout(() => { onRefresh?.(); }, 1000);
    } catch (e) {
      setRepairResult({ error: e.message });
    }
    setRepairing(false);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', borderRadius: expanded ? '8px 8px 0 0' : 8,
        backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a',
      }}>
        <span style={{ color: '#f87171', fontWeight: 700, fontSize: 13 }}>
          ⚠ {report.carousel_issues_total} Carousel Issue{report.carousel_issues_total !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setExpanded(!expanded)} style={{ ...s.toggleBtn, borderColor: '#3a1a1a', color: '#f87171' }}>
            {expanded ? '▼ Hide' : '▶ Show'}
          </button>
          <button onClick={runAutoRepair} disabled={repairing} style={{
            ...s.dangerBtn, fontSize: 11, padding: '4px 12px', opacity: repairing ? 0.5 : 1,
          }}>
            {repairing ? '⏳...' : '🔧 Auto-Repair'}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{
          maxHeight: 300, overflowY: 'auto', padding: '8px 0',
          backgroundColor: '#0f0808', border: '1px solid #3a1a1a', borderTop: 'none',
          borderRadius: '0 0 8px 8px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ color: '#5a3a3a', textAlign: 'left' }}>
                <th style={{ padding: '4px 10px' }}>Type</th>
                <th style={{ padding: '4px 10px' }}>Carousel</th>
                <th style={{ padding: '4px 10px' }}>Slide</th>
                <th style={{ padding: '4px 10px' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {report.carousel_issues.map((issue, i) => (
                <tr key={i} style={{ borderTop: '1px solid #1a0a0a' }}>
                  <td style={{ padding: '6px 10px', color: '#737373' }}>
                    {issue.issue.replace(/_/g, ' ')}
                  </td>
                  <td style={{ padding: '6px 10px', color: '#a0a0a0' }}>{issue.carousel_name || issue.carousel_id?.slice(0, 8)}</td>
                  <td style={{ padding: '6px 10px', color: '#737373' }}>#{issue.slide_index}</td>
                  <td style={{ padding: '6px 10px', color: '#525252', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {repairResult && (
        <div style={{
          padding: '10px 14px', marginTop: 6, borderRadius: 6, fontSize: 12,
          backgroundColor: repairResult.repaired > 0 ? '#0a140a' : '#140a0a',
          border: `1px solid ${repairResult.repaired > 0 ? '#1a3a1a' : '#3a1a1a'}`,
          color: repairResult.repaired > 0 ? '#4ade80' : '#f87171',
        }}>
          {repairResult.error
            ? `Error: ${repairResult.error}`
            : `✅ Repaired: ${repairResult.repaired}, Failed: ${repairResult.failed}`
          }
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Quarantine Panel ─────────────────────── */

function QuarantinePanel({ headers, onRefresh, envFilter }) {
  const [quarantine, setQuarantine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const fetchQuarantine = useCallback(async () => {
    setLoading(true);
    setActionResult(null);
    try {
      const r = await fetch(`${API}/api/admin/storage/quarantine`, { headers });
      setQuarantine(await r.json());
    } catch (e) {
      setQuarantine({ error: e.message });
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchQuarantine(); }, [fetchQuarantine]);

  const toggleBatch = (ts) => setExpanded(prev => ({ ...prev, [ts]: !prev[ts] }));

  const toggleKey = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleBatchAll = (keys) => {
    setSelectedKeys(prev => {
      const allSelected = keys.every(k => prev.has(k));
      if (allSelected) {
        const next = new Set(prev);
        keys.forEach(k => next.delete(k));
        return next;
      }
      return new Set([...prev, ...keys]);
    });
  };

  const executeRestore = async () => {
    setRestoring(true);
    try {
      const r = await fetch(`${API}/api/admin/storage/quarantine/restore`, {
        method: 'POST', headers,
        body: JSON.stringify({ keys: Array.from(selectedKeys) }),
      });
      const data = await r.json();
      setActionResult({ type: 'restore', ...data });
      setSelectedKeys(new Set());
      setTimeout(() => { fetchQuarantine(); onRefresh?.(); }, 1000);
    } catch (e) {
      setActionResult({ type: 'restore', error: e.message });
    }
    setRestoring(false);
  };

  const executePermanentDelete = async () => {
    setDeleting(true);
    setShowDeleteModal(false);
    try {
      const r = await fetch(`${API}/api/admin/storage/quarantine/delete`, {
        method: 'POST', headers,
        body: JSON.stringify({ keys: Array.from(selectedKeys), confirmation: 'DELETE' }),
      });
      const data = await r.json();
      setActionResult({ type: 'delete', ...data });
      setSelectedKeys(new Set());
      // Refresh quarantine list AND parent reconciliation
      setTimeout(() => { fetchQuarantine(); onRefresh?.(); }, 1000);
    } catch (e) {
      setActionResult({ type: 'delete', error: e.message });
    }
    setDeleting(false);
  };

  const [showProdWarningQ, setShowProdWarningQ] = useState(null);

  const requestPermanentDelete = () => {
    const keys = Array.from(selectedKeys);
    // Check for prod files by looking at original_key
    const allObjs = quarantine?.batches?.flatMap(b => b.objects) || [];
    const selectedObjs = allObjs.filter(o => keys.includes(o.quarantine_key));
    const prodObjs = selectedObjs.filter(o => !isDevKey(o.original_key || o.quarantine_key));
    if (prodObjs.length > 0) {
      setShowProdWarningQ({ action: 'permanent delete', prodCount: prodObjs.length });
    } else {
      setShowDeleteModal(true);
    }
  };

  if (!quarantine || quarantine.total_objects === 0) {
    return (
      <div style={{
        padding: '16px 20px', borderRadius: 8, marginTop: 20,
        backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Inbox size={16} color="#525252" />
        <span style={{ fontSize: 12, color: '#525252' }}>Quarantine is empty</span>
        <button onClick={fetchQuarantine} disabled={loading} style={{ ...s.toggleBtn, marginLeft: 'auto' }}>
          <RefreshCw size={10} /> Refresh
        </button>
      </div>
    );
  }

  const selectedSize = quarantine?.batches?.flatMap(b => b.objects)
    ?.filter(o => selectedKeys.has(o.quarantine_key))
    ?.reduce((sum, o) => sum + (o.size || 0), 0) || 0;

  return (
    <div style={{
      marginTop: 24, padding: 20, borderRadius: 10,
      backgroundColor: '#0c0c0c', border: '1px solid #2a1a1a',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#f97316' }}>
            <Archive size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            Quarantine
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#525252' }}>
            {quarantine.total_objects} objects ({quarantine.total_size_human}) — review and permanently delete or restore
          </p>
        </div>
        <button onClick={fetchQuarantine} disabled={loading} style={s.refreshBtn}>
          <RefreshCw size={12} /> {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Batches */}
      {quarantine.batches?.map(batch => {
        // Filter objects by env
        const filteredObjects = envFilter === 'all' ? batch.objects
          : batch.objects.filter(o => {
              const key = o.original_key || o.quarantine_key;
              return envFilter === 'dev' ? isDevKey(key) : !isDevKey(key);
            });
        if (filteredObjects.length === 0) return null;

        const isExpanded = expanded[batch.timestamp];
        const batchKeys = filteredObjects.map(o => o.quarantine_key);
        const allSelected = batchKeys.every(k => selectedKeys.has(k));

        return (
          <div key={batch.timestamp} style={{ marginBottom: 12 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 14px', borderRadius: isExpanded ? '8px 8px 0 0' : 8,
              backgroundColor: '#0f0a08', border: '1px solid #2a1a0a',
              cursor: 'pointer',
            }} onClick={() => toggleBatch(batch.timestamp)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Clock size={12} color="#f97316" />
                <span style={{ fontSize: 11, color: '#f97316', fontFamily: '"IBM Plex Mono", monospace' }}>
                  {batch.timestamp.replace(/T/g, ' ').replace(/-/g, (m, i) => i > 9 ? ':' : '-')}
                </span>
                <span style={{ fontSize: 11, color: '#525252' }}>
                  {filteredObjects.length}{envFilter !== 'all' && filteredObjects.length !== batch.count ? ` of ${batch.count}` : ''} files · {batch.total_size_human}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#5a3a1a' }}
                  onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => toggleBatchAll(batchKeys)}
                    style={{ accentColor: '#f97316', cursor: 'pointer' }}
                  />
                  Select batch
                </label>
                {isExpanded ? <ChevronUp size={12} color="#525252" /> : <ChevronDown size={12} color="#525252" />}
              </div>
            </div>
            {isExpanded && (
              <div style={{
                maxHeight: 400, overflowY: 'auto',
                backgroundColor: '#0a0808', border: '1px solid #2a1a0a', borderTop: 'none',
                borderRadius: '0 0 8px 8px',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: '#5a3a1a', textAlign: 'left', fontSize: 9, textTransform: 'uppercase' }}>
                      <th style={{ padding: '6px 10px', width: 32 }}></th>
                      <th style={{ padding: '6px 10px' }}>Env / Category</th>
                      <th style={{ padding: '6px 10px' }}>Filename</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right' }}>Size</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right' }}>Age</th>
                      <th style={{ padding: '6px 10px', width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredObjects.map(obj => {
                      const isRowExpanded = expandedRow === obj.quarantine_key;
                      return (
                        <React.Fragment key={obj.quarantine_key}>
                          <tr style={{
                            borderTop: '1px solid #1a0a0a',
                            backgroundColor: selectedKeys.has(obj.quarantine_key) ? '#140a08' : 'transparent',
                          }}>
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="checkbox"
                                checked={selectedKeys.has(obj.quarantine_key)}
                                onChange={() => toggleKey(obj.quarantine_key)}
                                style={{ accentColor: '#f97316', cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                <EnvBadge s3Key={obj.original_key || obj.quarantine_key} />
                                <CategoryBadge category={obj.category} label={obj.category_label} />
                              </div>
                            </td>
                            <td style={{
                              padding: '6px 10px', color: '#a08070', fontSize: 11,
                              maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontFamily: '"IBM Plex Mono", monospace',
                            }} title={obj.original_key}>
                              {obj.filename}
                            </td>
                            <td style={{ padding: '6px 10px', color: '#525252', textAlign: 'right' }}>
                              {obj.size_human}
                            </td>
                            <td style={{ padding: '6px 10px', color: '#525252', fontSize: 10, textAlign: 'right' }}>
                              {obj.age_days != null ? (
                                obj.age_days === 0 ? `${Math.round(obj.age_hours || 0)}h` : `${obj.age_days}d`
                              ) : '—'}
                            </td>
                            <td style={{ padding: '6px 10px', width: 40, textAlign: 'center' }}>
                              <button
                                onClick={() => setExpandedRow(isRowExpanded ? null : obj.quarantine_key)}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                                  color: isRowExpanded ? '#f97316' : '#3a3a3a',
                                }}
                              >
                                {isRowExpanded ? <ChevronUp size={14} /> : <Eye size={14} />}
                              </button>
                            </td>
                          </tr>
                          {isRowExpanded && (
                            <tr>
                              <td colSpan={6} style={{
                                padding: 0,
                                backgroundColor: '#080606', borderTop: 'none',
                              }}>
                                <FileDetailPanel
                                  s3Key={obj.quarantine_key}
                                  originalKey={obj.original_key}
                                  size={obj.size}
                                  sizeHuman={obj.size_human}
                                  lastModified={obj.last_modified}
                                  ageHours={obj.age_hours}
                                  ageDays={obj.age_days}
                                  category={obj.category}
                                  categoryLabel={obj.category_label}
                                  headers={headers}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Action bar */}
      {selectedKeys.size > 0 && (
        <div style={{
          position: 'sticky', bottom: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', marginTop: 12, borderRadius: 8,
          backgroundColor: '#140a0a', border: '1px solid #3a1a0a',
        }}>
          <div style={{ fontSize: 12, color: '#a0a0a0' }}>
            <strong style={{ color: '#f97316' }}>{selectedKeys.size}</strong> selected
            <span style={{ color: '#525252', marginLeft: 8 }}>({humanSize(selectedSize)})</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={executeRestore}
              disabled={restoring}
              style={{ ...s.actionBtn, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <ArchiveRestore size={12} />
              {restoring ? 'Restoring...' : `Restore ${selectedKeys.size}`}
            </button>
            <button
              onClick={requestPermanentDelete}
              disabled={deleting}
              style={{ ...s.dangerBtn, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Trash2 size={12} />
              {deleting ? 'Deleting...' : `Delete permanently`}
            </button>
          </div>
        </div>
      )}

      {/* Action result */}
      {actionResult && (
        <div style={{
          padding: '12px 16px', marginTop: 12, borderRadius: 8,
          backgroundColor: actionResult.error ? '#1a0a0a'
            : actionResult.type === 'restore' ? '#0a140a' : '#0a0a14',
          border: `1px solid ${actionResult.error ? '#3a1a1a'
            : actionResult.type === 'restore' ? '#1a3a1a' : '#1a1a3a'}`,
        }}>
          {actionResult.error ? (
            <span style={{ color: '#f87171', fontSize: 12 }}>Error: {actionResult.error}</span>
          ) : actionResult.type === 'restore' ? (
            <span style={{ color: '#4ade80', fontSize: 12 }}>
              ✅ Restored {actionResult.restored} objects
              {actionResult.errors > 0 && <span style={{ color: '#f87171' }}> · {actionResult.errors} errors</span>}
            </span>
          ) : (
            <span style={{ color: '#60a5fa', fontSize: 12 }}>
              ✅ Permanently deleted {actionResult.deleted} objects ({actionResult.deleted_size_human})
              {actionResult.errors > 0 && <span style={{ color: '#f87171' }}> · {actionResult.errors} errors</span>}
            </span>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <ConfirmModal
          title={`Permanently Delete ${selectedKeys.size} Objects`}
          description={
            <div>
              <p>This will <strong style={{ color: '#f87171' }}>permanently delete {selectedKeys.size} objects</strong> totaling <strong style={{ color: '#fbbf24' }}>{humanSize(selectedSize)}</strong> from quarantine.</p>
              <p style={{ color: '#f87171', fontWeight: 600, marginTop: 8 }}>This action cannot be undone.</p>
            </div>
          }
          confirmLabel={`Delete ${selectedKeys.size} objects`}
          confirmColor="#f87171"
          requireTyped="DELETE"
          onConfirm={executePermanentDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
      {showProdWarningQ && (
        <ProdWarningModal
          action={showProdWarningQ.action}
          prodCount={showProdWarningQ.prodCount}
          onConfirm={() => { setShowProdWarningQ(null); setShowDeleteModal(true); }}
          onCancel={() => setShowProdWarningQ(null)}
        />
      )}
    </div>
  );
}

/* ─────────────────────── Triage Panel ─────────────────────── */

const TRIAGE_ACTION_ICONS = {
  register_backup: Database,
  quarantine: Archive,
  delete: Trash2,
  investigate: Search,
};

function TriageGroupCard({ group, headers, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [acting, setActing] = useState(false);
  const [result, setResult] = useState(null);

  const ActionIcon = TRIAGE_ACTION_ICONS[group.action_type] || Search;

  const executeRegisterBackup = async () => {
    setActing(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/storage/triage/register-backups`, {
        method: 'POST', headers,
        body: JSON.stringify({ files: [] }), // auto-discover
      });
      const data = await r.json();
      setResult({ type: 'register_backup', ...data });
      if (data.registered > 0) setTimeout(() => onRefresh?.(), 1500);
    } catch (e) {
      setResult({ type: 'register_backup', error: e.message });
    }
    setActing(false);
  };

  const executeQuarantineGroup = async () => {
    setActing(true);
    setShowDeleteModal(false);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/storage/triage/quarantine-group`, {
        method: 'POST', headers,
        body: JSON.stringify({
          triage_group: group.triage_group,
          confirmation: 'QUARANTINE',
          force: group.action_type === 'investigate',
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setResult({ type: 'quarantine', ...data });
        if (data.quarantined > 0) setTimeout(() => onRefresh?.(), 1500);
      } else {
        setResult({ type: 'quarantine', error: data.detail || 'Failed' });
      }
    } catch (e) {
      setResult({ type: 'quarantine', error: e.message });
    }
    setActing(false);
  };

  return (
    <div style={{
      borderRadius: 10, overflow: 'hidden',
      border: `1px solid ${group.color}22`,
      backgroundColor: '#0a0a0a',
      marginBottom: 12,
    }}>
      {/* Group header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: `${group.color}08`,
        borderBottom: expanded ? `1px solid ${group.color}15` : 'none',
        cursor: 'pointer',
      }} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {/* Count badge */}
          <div style={{
            minWidth: 40, height: 40, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: `${group.color}15`,
            border: `1px solid ${group.color}30`,
          }}>
            <span style={{
              fontSize: 18, fontWeight: 700, color: group.color,
              fontFamily: '"IBM Plex Mono", monospace',
            }}>
              {group.count}
            </span>
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: group.color }}>
                {group.label}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                backgroundColor: group.safe ? '#0a1a0a' : '#1a0f0a',
                border: `1px solid ${group.safe ? '#1a3a1a' : '#3a2a1a'}`,
                color: group.safe ? '#4ade80' : '#fbbf24',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {group.safe ? 'SAFE' : 'REVIEW'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#525252', marginTop: 2, lineHeight: 1.4 }}>
              {group.description.length > 120 && !expanded
                ? group.description.slice(0, 120) + '...'
                : group.description}
            </div>
          </div>

          {/* Size */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: '#a0a0a0',
              fontFamily: '"IBM Plex Mono", monospace',
            }}>
              {group.total_size_human}
            </div>
            <div style={{ fontSize: 9, color: '#3a3a3a' }}>
              {group.count} file{group.count !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12, flexShrink: 0 }}>
          {/* Action button */}
          {/* Register Backup button — only for backup_untracked groups */}
          {group.action_type === 'register_backup' && (
            <button
              onClick={(e) => { e.stopPropagation(); executeRegisterBackup(); }}
              disabled={acting}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 6,
                border: '1px solid #2a1a4a',
                backgroundColor: '#0a0a1a', color: '#818cf8',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                opacity: acting ? 0.5 : 1,
              }}
            >
              <Database size={11} />
              {acting ? 'Registering...' : group.action_label}
            </button>
          )}
          {/* Quarantine button — available for ALL group types with pre-verification */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
            disabled={acting}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 6,
              border: '1px solid #5a3a1a',
              backgroundColor: '#1a0a04', color: '#f97316',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              opacity: acting ? 0.5 : 1,
            }}
            title="Move to quarantine (verified safe + reversible)"
          >
            <Archive size={11} />
            {acting ? 'Quarantining...' : 'Quarantine'}
          </button>

          {/* Expand chevron */}
          {expanded ? <ChevronUp size={14} color="#3a3a3a" /> : <ChevronDown size={14} color="#3a3a3a" />}
        </div>
      </div>

      {/* Expanded items */}
      {expanded && (
        <div style={{
          maxHeight: 400, overflowY: 'auto',
          backgroundColor: '#080808',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{
                color: '#3a3a3a', textAlign: 'left', fontSize: 9,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                <th style={{ padding: '6px 14px' }}>Env / Category</th>
                <th style={{ padding: '6px 10px' }}>Filename</th>
                <th style={{ padding: '6px 10px' }}>Root Cause</th>
                <th style={{ padding: '6px 10px', textAlign: 'right' }}>Size</th>
                <th style={{ padding: '6px 10px', textAlign: 'right' }}>Age</th>
                <th style={{ padding: '6px 10px', width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {group.items.map((item, i) => {
                const isItemExpanded = expandedItem === item.key;
                return (
                  <React.Fragment key={item.key}>
                    <tr style={{ borderTop: '1px solid #111' }}>
                      <td style={{ padding: '5px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                          <EnvBadge s3Key={item.key} />
                          <CategoryBadge category={item.category} label={item.category_label} />
                        </div>
                      </td>
                      <td style={{
                        padding: '5px 10px', color: '#a0a0a0',
                        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontFamily: '"IBM Plex Mono", monospace', fontSize: 10,
                      }} title={item.key}>
                        {item.filename}
                      </td>
                      <td style={{ padding: '5px 10px', color: '#525252', fontSize: 10, maxWidth: 260 }}>
                        {item.detail?.length > 70 ? item.detail.slice(0, 70) + '...' : item.detail}
                      </td>
                      <td style={{ padding: '5px 10px', color: '#737373', textAlign: 'right', fontSize: 10 }}>
                        {item.size_human}
                      </td>
                      <td style={{ padding: '5px 10px', color: '#525252', textAlign: 'right', fontSize: 10 }}>
                        {item.age_days !== null && item.age_days !== undefined ? (
                          item.age_days === 0 ? `${Math.round(item.age_hours || 0)}h` : `${item.age_days}d`
                        ) : '—'}
                      </td>
                      <td style={{ padding: '5px 10px', width: 36, textAlign: 'center' }}>
                        <button
                          onClick={() => setExpandedItem(isItemExpanded ? null : item.key)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                            color: isItemExpanded ? group.color : '#3a3a3a',
                          }}
                        >
                          {isItemExpanded ? <ChevronUp size={13} /> : <Eye size={13} />}
                        </button>
                      </td>
                    </tr>
                    {isItemExpanded && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, backgroundColor: '#060606', borderTop: 'none' }}>
                          <FileDetailPanel
                            s3Key={item.key}
                            size={item.size}
                            sizeHuman={item.size_human}
                            lastModified={item.last_modified}
                            ageHours={item.age_hours}
                            ageDays={item.age_days}
                            category={item.category}
                            categoryLabel={item.category_label}
                            headers={headers}
                          >
                            {item.detail && (
                              <div style={{
                                marginTop: 6, padding: '6px 10px', borderRadius: 4,
                                backgroundColor: '#0a0a14', border: '1px solid #1a1a3a',
                                fontSize: 10, color: '#60a5fa', lineHeight: 1.5,
                              }}>
                                <span style={{ fontWeight: 600 }}>Root cause:</span> {item.detail}
                              </div>
                            )}
                          </FileDetailPanel>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Result toast */}
      {result && (
        <div style={{
          padding: '10px 16px', fontSize: 12,
          backgroundColor: result.error ? '#1a0808' : result.type === 'track' ? '#080f14' : '#0a140a',
          borderTop: `1px solid ${result.error ? '#3a1a1a' : result.type === 'track' ? '#1a2a3a' : '#1a3a1a'}`,
          color: result.error ? '#f87171' : result.type === 'track' ? '#818cf8' : '#4ade80',
        }}>
          {result.error ? (
            `Error: ${result.error}`
          ) : result.type === 'register_backup' ? (
            `✅ Registered ${result.registered} backup file(s) in system_backups. Already tracked: ${result.already_tracked}.${result.message ? ` ${result.message}` : ''}`
          ) : result.type === 'track' ? (
            `✅ Registered ${result.registered} system file(s). Already tracked: ${result.already_tracked}.`
          ) : (
            `✅ Quarantined ${result.quarantined} file(s) (${result.quarantined_size_human}).${result.skipped > 0 ? ` Skipped: ${result.skipped} (failed verification).` : ''} Errors: ${result.errors}. Review in Quarantine panel below.`
          )}
        </div>
      )}

      {/* Quarantine confirmation modal */}
      {showDeleteModal && (
        <ConfirmModal
          title={`Quarantine "${group.label}" Group (${group.count} files)`}
          description={
            <div>
              <p>This will move <strong style={{ color: '#f97316' }}>{group.count} S3 objects</strong> totaling <strong style={{ color: '#fbbf24' }}>{group.total_size_human}</strong> to quarantine.</p>
              <div style={{
                margin: '10px 0', padding: '8px 12px', borderRadius: 6,
                backgroundColor: '#0a0a14', border: '1px solid #1a1a3a',
                fontSize: 11, color: '#60a5fa', lineHeight: 1.6,
              }}>
                <strong>Triage analysis:</strong> {group.description}
              </div>
              <div style={{
                margin: '8px 0', padding: '8px 12px', borderRadius: 6,
                backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
                fontSize: 11, color: '#4ade80',
              }}>
                ✓ <strong>Pre-quarantine verification</strong> — each file is checked against all tracking sources and carousel references before moving.
              </div>
              <div style={{
                margin: '8px 0', padding: '8px 12px', borderRadius: 6,
                backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
                fontSize: 11, color: '#4ade80',
              }}>
                ✓ <strong>This is reversible</strong> — files can be restored from the Quarantine panel at any time.
              </div>
              {!group.safe && (
                <div style={{
                  margin: '8px 0', padding: '8px 12px', borderRadius: 6,
                  backgroundColor: '#1a0f04', border: '1px solid #3a2a1a',
                  fontSize: 11, color: '#fbbf24',
                }}>
                  ⚠ This group is marked as "REVIEW" — proceed only after manual investigation.
                </div>
              )}
              {(() => {
                const prodItems = group.items.filter(i => !isDevKey(i.key));
                return prodItems.length > 0 ? (
                  <div style={{
                    margin: '8px 0', padding: '8px 12px', borderRadius: 6,
                    backgroundColor: '#1a0f04', border: '1px solid #3a2a1a',
                    fontSize: 11, color: '#f59e0b',
                  }}>
                    ⚠ <strong>{prodItems.length} PRODUCTION file{prodItems.length !== 1 ? 's' : ''}</strong> included.
                  </div>
                ) : null;
              })()}
            </div>
          }
          confirmLabel={`Quarantine ${group.count} files`}
          confirmColor="#f97316"
          requireTyped="QUARANTINE"
          onConfirm={executeQuarantineGroup}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}


function TriagePanel({ headers, onRefresh, envFilter }) {
  const [triage, setTriage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runTriage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/admin/storage/triage`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setTriage(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [headers]);

  const handleGroupAction = () => {
    // After a group action completes, re-run triage + parent refresh
    runTriage();
    onRefresh?.();
  };

  // Apply env filter to triage groups
  const filterByEnv = useCallback((item) => {
    if (envFilter === 'all') return true;
    if (envFilter === 'dev') return isDevKey(item.key);
    return !isDevKey(item.key); // prod
  }, [envFilter]);

  // Build filtered groups with recalculated counts/sizes
  const allGroups = triage?.groups ? Object.values(triage.groups) : [];
  const filteredGroups = allGroups.map(g => {
    if (envFilter === 'all') return g;
    const filteredItems = g.items.filter(filterByEnv);
    const filteredSize = filteredItems.reduce((sum, i) => sum + (i.size || 0), 0);
    return {
      ...g,
      items: filteredItems,
      count: filteredItems.length,
      total_size: filteredSize,
      total_size_human: filteredSize > 1024 * 1024
        ? `${(filteredSize / (1024 * 1024)).toFixed(1)} MB`
        : filteredSize > 1024
          ? `${(filteredSize / 1024).toFixed(1)} KB`
          : `${filteredSize} B`,
    };
  }).filter(g => g.count > 0);

  const groups = filteredGroups;
  const safeGroups = groups.filter(g => g.safe);
  const reviewGroups = groups.filter(g => !g.safe);

  // Summary uses filtered counts
  const totalFilteredOrphans = groups.reduce((sum, g) => sum + g.count, 0);
  const totalFilteredSize = groups.reduce((sum, g) => sum + (g.total_size || 0), 0);
  const totalFilteredSizeHuman = totalFilteredSize > 1024 * 1024
    ? `${(totalFilteredSize / (1024 * 1024)).toFixed(1)} MB`
    : totalFilteredSize > 1024
      ? `${(totalFilteredSize / 1024).toFixed(1)} KB`
      : `${totalFilteredSize} B`;

  return (
    <div style={{
      marginTop: 24, padding: 20, borderRadius: 10,
      backgroundColor: '#0c0c0c', border: '1px solid #1a1a2a',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <div>
          <h3 style={{
            margin: 0, fontSize: 15, fontWeight: 700, color: '#818cf8',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Layers size={16} />
            Orphan Triage
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#525252' }}>
            Classify orphans by root cause · Group actions · Human-verified cleanup
          </p>
        </div>
        <button
          onClick={runTriage}
          disabled={loading}
          style={{ ...s.actionBtn, borderColor: '#2a2a5a', backgroundColor: '#0a0a1a', color: '#818cf8', opacity: loading ? 0.5 : 1 }}
        >
          <Layers size={12} style={{ marginRight: 4 }} />
          {loading ? 'Analyzing...' : triage ? 'Re-analyze' : 'Run Triage'}
        </button>
      </div>

      {/* Info banner when no triage run yet */}
      {!triage && !loading && !error && (
        <div style={{
          padding: '14px 16px', borderRadius: 8,
          backgroundColor: '#0a0a14', border: '1px solid #1a1a3a',
          fontSize: 12, color: '#818cf8', lineHeight: 1.7,
        }}>
          <strong>Root-cause triage classifies every orphan into actionable groups:</strong>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px',
            marginTop: 10, fontSize: 11, color: '#6060a0',
          }}>
            <div>🟣 <strong>Untracked Backups</strong> — register in system_backups</div>
            <div>⚫ <strong>Test Artifacts</strong> — quarantine and delete</div>
            <div>🔵 <strong>Old Exports</strong> — pre-tracking, safe to delete</div>
            <div>🟢 <strong>Dev/Sandbox</strong> — test data, safe to delete</div>
            <div>🟢 <strong>Stale Presets</strong> — replaced, safe to delete</div>
            <div>🟡 <strong>Tracking Bug</strong> — investigate code path first</div>
            <div>🟠 <strong>Migration Dups</strong> — may need DB repointing</div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{
          padding: 20, textAlign: 'center', fontSize: 13, color: '#818cf8',
        }}>
          <div style={{ marginBottom: 8 }}>⏳ Analyzing orphans and classifying by root cause...</div>
          <div style={{ fontSize: 10, color: '#525252' }}>
            Running S3 listing, DB queries, and per-orphan triage classification
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 6,
          backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a',
          color: '#f87171', fontSize: 12,
        }}>
          Error: {error}
        </div>
      )}

      {/* Results */}
      {triage && !loading && (
        <div>
          {/* Summary bar */}
          <div style={{
            display: 'flex', gap: 10, marginBottom: 16,
            padding: '12px 16px', borderRadius: 8,
            backgroundColor: '#050508', border: '1px solid #151520',
          }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: '#818cf8',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {totalFilteredOrphans}
              </div>
              <div style={{ fontSize: 9, color: '#525252' }}>
                {envFilter !== 'all' ? `${envFilter.toUpperCase()} Orphans` : 'Total Orphans'}
              </div>
            </div>
            <div style={{ width: 1, backgroundColor: '#1a1a2a' }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: '#fafafa',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {groups.length}
              </div>
              <div style={{ fontSize: 9, color: '#525252' }}>Groups</div>
            </div>
            <div style={{ width: 1, backgroundColor: '#1a1a2a' }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: '#4ade80',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {safeGroups.reduce((sum, g) => sum + g.count, 0)}
              </div>
              <div style={{ fontSize: 9, color: '#3a5a3a' }}>Safe to Act</div>
            </div>
            <div style={{ width: 1, backgroundColor: '#1a1a2a' }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: '#fbbf24',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {reviewGroups.reduce((sum, g) => sum + g.count, 0)}
              </div>
              <div style={{ fontSize: 9, color: '#5a5a2a' }}>Needs Review</div>
            </div>
            <div style={{ width: 1, backgroundColor: '#1a1a2a' }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: '#a0a0a0',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {envFilter !== 'all' ? totalFilteredSizeHuman : triage.total_size_human}
              </div>
              <div style={{ fontSize: 9, color: '#525252' }}>Total Size</div>
            </div>
          </div>

          {triage.orphans_capped && (
            <div style={{
              padding: '8px 14px', borderRadius: 6, marginBottom: 12,
              backgroundColor: '#1a0f04', border: '1px solid #3a2a1a',
              fontSize: 11, color: '#fbbf24',
            }}>
              ⚠ Showing {triage.total_orphans} of {triage.orphans_total_actual} total orphans (API cap at 100).
              Some groups may have additional files beyond what's shown.
            </div>
          )}

          {/* Zero orphans — all clean */}
          {totalFilteredOrphans === 0 && triage.total_orphans > 0 && envFilter !== 'all' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
              borderRadius: 8, backgroundColor: '#0a0a14', border: '1px solid #1a1a3a',
            }}>
              <Shield size={18} color="#818cf8" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#818cf8' }}>
                No {envFilter.toUpperCase()} orphans — {triage.total_orphans} orphan{triage.total_orphans !== 1 ? 's' : ''} in other environment{triage.total_orphans !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {totalFilteredOrphans === 0 && (triage.total_orphans === 0 || envFilter === 'all') && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
              borderRadius: 8, backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
            }}>
              <CheckCircle size={18} color="#4ade80" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4ade80' }}>
                Zero orphans — all S3 objects are tracked!
              </span>
            </div>
          )}

          {/* Env filter active indicator */}
          {envFilter !== 'all' && totalFilteredOrphans > 0 && totalFilteredOrphans < triage.total_orphans && (
            <div style={{
              padding: '6px 14px', borderRadius: 6, marginBottom: 12,
              backgroundColor: '#0a0a14', border: '1px solid #1a1a3a',
              fontSize: 11, color: '#818cf8',
            }}>
              Showing {totalFilteredOrphans} of {triage.total_orphans} orphans ({envFilter.toUpperCase()} only)
            </div>
          )}

          {/* Safe groups first */}
          {safeGroups.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#4ade80', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <ShieldCheck size={12} />
                Safe to Act ({safeGroups.length} groups)
              </div>
              {safeGroups.map(g => (
                <TriageGroupCard
                  key={g.triage_group}
                  group={g}
                  headers={headers}
                  onRefresh={handleGroupAction}
                />
              ))}
            </div>
          )}

          {/* Review groups */}
          {reviewGroups.length > 0 && (
            <div>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <AlertTriangle size={12} />
                Needs Investigation ({reviewGroups.length} groups)
              </div>
              {reviewGroups.map(g => (
                <TriageGroupCard
                  key={g.triage_group}
                  group={g}
                  headers={headers}
                  onRefresh={handleGroupAction}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────── Main Page ─────────────────────── */

export default function AdminStorage() {
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [password, setPassword] = useState('');
  const inputRef = useRef(null);

  // Reconciliation state
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Validation state
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  // Quarantine state
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showQuarantineModal, setShowQuarantineModal] = useState(false);
  const [quarantining, setQuarantining] = useState(false);
  const [quarantineResult, setQuarantineResult] = useState(null);
  const [quarantineRefreshKey, setQuarantineRefreshKey] = useState(0);

  // Environment filter
  const [envFilter, setEnvFilter] = useState('all'); // 'all' | 'prod' | 'dev'

  // Prod warning modal
  const [prodWarning, setProdWarning] = useState(null); // { action, onConfirm }

  const headers = {
    'X-Admin-Password': password,
    'Content-Type': 'application/json',
  };

  const handleLogin = async (pw) => {
    try {
      const r = await fetch(`${API}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (r.ok) {
        setPassword(pw);
        setAuthed(true);
        setAuthError('');
      } else {
        setAuthError('Invalid password');
      }
    } catch (e) {
      setAuthError('Connection error');
    }
  };

  // ── Reconciliation ──
  const runReconciliation = useCallback(async () => {
    setLoading(true);
    setQuarantineResult(null);
    // Clear stale validation state — orphan list changes after reconciliation
    setValidation(null);
    setSelectedKeys(new Set());
    try {
      const r = await fetch(`${API}/api/admin/storage/reconcile`, { headers });
      const data = await r.json();
      setReport(data);
    } catch (e) {
      setReport({ error: e.message });
    }
    setLoading(false);
  }, [password]);

  // ── Validation ──
  const runValidation = useCallback(async () => {
    setValidating(true);
    setSelectedKeys(new Set());
    setQuarantineResult(null);
    try {
      const r = await fetch(`${API}/api/admin/storage/validate-orphans`, {
        method: 'POST', headers,
      });
      const data = await r.json();
      setValidation(data);
    } catch (e) {
      setValidation({ error: e.message });
    }
    setValidating(false);
  }, [password]);

  // ── Selection ──
  const toggleKey = useCallback((key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback((keys) => {
    setSelectedKeys(prev => {
      const allSelected = keys.every(k => prev.has(k));
      if (allSelected) return new Set();
      return new Set(keys);
    });
  }, []);

  // ── Quarantine (Stage 1) ──
  const executeQuarantine = useCallback(async () => {
    setQuarantining(true);
    setShowQuarantineModal(false);
    try {
      const r = await fetch(`${API}/api/admin/storage/quarantine`, {
        method: 'POST', headers,
        body: JSON.stringify({ keys: Array.from(selectedKeys) }),
      });
      const data = await r.json();
      setQuarantineResult(data);
      setSelectedKeys(new Set());
      // Refresh everything
      setTimeout(() => {
        runReconciliation();
        runValidation();
        setQuarantineRefreshKey(k => k + 1);
      }, 1000);
    } catch (e) {
      setQuarantineResult({ error: e.message });
    }
    setQuarantining(false);
  }, [selectedKeys, password, runReconciliation, runValidation]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Quarantine with prod warning ──
  const requestQuarantine = useCallback(() => {
    const keys = Array.from(selectedKeys);
    const prodKeys = keys.filter(k => !isDevKey(k));
    if (prodKeys.length > 0) {
      setProdWarning({
        action: 'quarantine',
        prodCount: prodKeys.length,
        onConfirm: () => setShowQuarantineModal(true),
      });
    } else {
      setShowQuarantineModal(true);
    }
  }, [selectedKeys]);

  // Auto-run reconciliation on login
  useEffect(() => {
    if (authed) runReconciliation();
  }, [authed, runReconciliation]);

  // ── Login screen ──
  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' }}>
        <div style={{ padding: 40, borderRadius: 12, width: 340, textAlign: 'center', backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🗄️</div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fafafa' }}>Magic Forest</h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#525252' }}>Storage Health</p>
          <form onSubmit={e => { e.preventDefault(); handleLogin(e.target.querySelector('input').value); }} style={{ width: '100%', marginTop: 24 }}>
            <input
              ref={inputRef}
              type="password" placeholder="Admin password"
              style={{ width: '100%', padding: '11px 14px', borderRadius: 6, border: '1px solid #1f1f1f', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'Inter, system-ui, sans-serif' }}
            />
            {authError && <p style={{ color: '#f87171', fontSize: 12, margin: '8px 0' }}>{authError}</p>}
            <button type="submit" style={{ width: '100%', padding: '11px 16px', borderRadius: 6, border: 'none', backgroundColor: '#4ade80', color: '#0a0a0a', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  const selectedSize = validation?.orphans
    ?.filter(o => selectedKeys.has(o.key))
    ?.reduce((sum, o) => sum + (o.size || 0), 0) || 0;

  // Filter orphans and db_orphans by env
  const filteredOrphans = (validation?.orphans || []).filter(o => {
    if (envFilter === 'all') return true;
    if (envFilter === 'dev') return isDevKey(o.key);
    return !isDevKey(o.key);
  });
  const filteredDbOrphans = (report?.db_orphans || []).filter(o => {
    if (envFilter === 'all') return true;
    const key = o.storage_key || '';
    if (envFilter === 'dev') return isDevKey(key);
    return !isDevKey(key);
  });
  const filteredS3Orphans = (report?.s3_orphans || []).filter(o => {
    if (envFilter === 'all') return true;
    if (envFilter === 'dev') return isDevKey(o.key);
    return !isDevKey(o.key);
  });

  return (
    <AdminLayout activePage="storage" onLogout={() => { setAuthed(false); setPassword(''); }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#525252' }}>
            S3 ↔ DB reconciliation · Orphan validation · Safe cleanup
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <AgentLogButton headers={headers} />
          <button onClick={runReconciliation} disabled={loading} style={s.refreshBtn}>
            <RefreshCw size={12} className={loading ? 'spin' : ''} />
            {loading ? 'Scanning...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {report && !report.error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
          <SummaryCard
            label="S3 Objects" value={report.s3_summary?.total_objects}
            sub={report.s3_summary?.total_size_human} color="#4ade80" icon={HardDrive}
          />
          <SummaryCard
            label="Tracked" value={report.healthy}
            sub="across 4 sources" color="#4ade80" icon={CheckCircle}
          />
          <SummaryCard
            label="True Orphans" value={report.s3_orphans_total}
            color={report.s3_orphans_total > 0 ? '#fbbf24' : '#525252'} icon={AlertTriangle}
          />
          <SummaryCard
            label="DB Records" value={report.db_summary?.total_records}
            sub={`${report.db_summary?.confirmed_with_key || report.db_summary?.with_storage_key} active${report.db_summary?.soft_deleted ? ` · ${report.db_summary.soft_deleted} deleted` : ''}${report.db_summary?.stale_uploads ? ` · ${report.db_summary.stale_uploads} stale` : ''}`} color="#60a5fa" icon={Database}
          />
          {report.quarantine_count > 0 && (
            <SummaryCard
              label="Quarantined" value={report.quarantine_count}
              sub={report.quarantine_size_human} color="#f97316" icon={Archive}
            />
          )}
          <SummaryCard
            label="Carousel Issues" value={report.carousel_issues_total}
            color={report.carousel_issues_total > 0 ? '#f87171' : '#525252'} icon={XCircle}
          />
        </div>
      )}

      {/* Tracking Breakdown (Option C — 4-source domain-owned tracking) */}
      {report && !report.error && report.tracking_breakdown && (
        <TrackingBreakdown breakdown={report.tracking_breakdown} />
      )}

      {/* Environment filter */}
      {report && !report.error && (
        <EnvFilterBar envFilter={envFilter} setEnvFilter={setEnvFilter} report={report} />
      )}

      {report?.error && (
        <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a', color: '#f87171', fontSize: 12, marginBottom: 16 }}>
          Error: {report.error}
        </div>
      )}

      {/* All healthy banner */}
      {report && !report.error && report.carousel_issues_total === 0 && report.s3_orphans_total === 0 && report.db_orphans_total === 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
          borderRadius: 8, backgroundColor: '#0a140a', border: '1px solid #1a3a1a', marginBottom: 16,
        }}>
          <CheckCircle size={18} color="#4ade80" />
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#4ade80' }}>All Tracked</span>
            <span style={{ fontSize: 11, color: '#3a5a3a', marginLeft: 10 }}>
              {report.healthy} S3 objects tracked, 0 true orphans, {report.carousels_scanned} carousels verified
            </span>
          </div>
        </div>
      )}

      {/* Carousel Issues */}
      {report && <CarouselIssuesSection report={report} headers={headers} onRefresh={runReconciliation} />}

      {/* DB Orphans */}
      {report && <DbOrphansSection report={report} />}

      {/* ── S3 Orphan Validation Section ── */}
      {report && !report.error && report.s3_orphans_total > 0 && (
        <div style={{
          marginTop: 24, padding: 20, borderRadius: 10,
          backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#fbbf24' }}>
                True Orphan Validation
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#525252' }}>
                {report.s3_orphans_total} S3 objects not tracked in any source
                {envFilter !== 'all' && filteredS3Orphans.length !== report.s3_orphans_total
                  ? ` · showing ${filteredS3Orphans.length} ${envFilter.toUpperCase()}`
                  : ''
                }
                {' — validate before deletion'}
              </p>
            </div>
            <button
              onClick={runValidation}
              disabled={validating}
              style={{ ...s.actionBtn, opacity: validating ? 0.5 : 1 }}
            >
              <Search size={12} style={{ marginRight: 4 }} />
              {validating ? 'Validating...' : validation ? 'Re-validate' : 'Validate Orphans'}
            </button>
          </div>

          {/* Validation info banner */}
          {!validation && !validating && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              backgroundColor: '#0a0d14', border: '1px solid #1a2a3a',
              fontSize: 12, color: '#60a5fa', lineHeight: 1.6,
            }}>
              <strong>8-layer hardened validation checks each orphan against:</strong>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', marginTop: 8, fontSize: 11 }}>
                <div>✓ L1: No database record</div>
                <div>✓ L2: No carousel JSONB ref (enhanced: stem + fragments)</div>
                <div>✓ L3: No render job output reference</div>
                <div>✓ L4: Category safe (proxies always blocked)</div>
                <div>✓ L5: Age analysis (flag recent &lt;24h)</div>
                <div>✓ L6: Filename not shared with any DB record</div>
                <div>✓ L7: No asset_references table entries</div>
                <div>✓ L8: No reverse asset chain (key → carousel)</div>
              </div>
            </div>
          )}

          {/* Validation loading */}
          {validating && (
            <div style={{
              padding: '20px', textAlign: 'center', fontSize: 13, color: '#fbbf24',
            }}>
              <div style={{ marginBottom: 8 }}>⏳ Running 8-layer hardened validation...</div>
              <div style={{ fontSize: 10, color: '#525252' }}>
                Scanning {report.s3_orphans_total} orphans against carousels, render jobs, asset references, and reverse chains
              </div>
            </div>
          )}

          {/* Validation results */}
          {validation && !validation.error && (
            <div>
              {/* Validation summary */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16,
              }}>
                <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#fbbf24', fontFamily: '"IBM Plex Mono", monospace' }}>{validation.total}</div>
                  <div style={{ fontSize: 9, color: '#525252' }}>Total Orphans</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: '#0a140a', border: '1px solid #1a3a1a', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#4ade80', fontFamily: '"IBM Plex Mono", monospace' }}>{validation.safe_count}</div>
                  <div style={{ fontSize: 9, color: '#3a5a3a' }}>Safe to Delete</div>
                  <div style={{ fontSize: 9, color: '#2a3a2a' }}>{validation.safe_size_human}</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: validation.unsafe_count > 0 ? '#1a0a0a' : '#0a0a0a', border: `1px solid ${validation.unsafe_count > 0 ? '#3a1a1a' : '#1a1a1a'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: validation.unsafe_count > 0 ? '#f87171' : '#525252', fontFamily: '"IBM Plex Mono", monospace' }}>{validation.unsafe_count}</div>
                  <div style={{ fontSize: 9, color: validation.unsafe_count > 0 ? '#5a2a2a' : '#525252' }}>Unsafe</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#a0a0a0', fontFamily: '"IBM Plex Mono", monospace' }}>{validation.total_size_human}</div>
                  <div style={{ fontSize: 9, color: '#525252' }}>Total Size</div>
                </div>
              </div>

              {/* Category breakdown */}
              <CategoryBreakdown breakdown={validation.category_breakdown} />

              {/* Orphan table */}
              <OrphanTable
                orphans={filteredOrphans}
                selectedKeys={selectedKeys}
                onToggleKey={toggleKey}
                onToggleAll={toggleAll}
                headers={headers}
              />

              {/* Quarantine action bar */}
              {selectedKeys.size > 0 && (
                <div style={{
                  position: 'sticky', bottom: 0,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', marginTop: 12, borderRadius: 8,
                  backgroundColor: '#140a08', border: '1px solid #3a1a0a',
                }}>
                  <div style={{ fontSize: 12, color: '#a0a0a0' }}>
                    <strong style={{ color: '#f97316' }}>{selectedKeys.size}</strong> objects selected
                    <span style={{ color: '#525252', marginLeft: 8 }}>
                      ({humanSize(selectedSize)})
                    </span>
                  </div>
                  <button
                    onClick={requestQuarantine}
                    disabled={quarantining}
                    style={{ ...s.quarantineBtn, display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Archive size={12} />
                    {quarantining ? 'Moving...' : `Quarantine ${selectedKeys.size} objects`}
                  </button>
                </div>
              )}

              {/* Quarantine result */}
              {quarantineResult && (
                <div style={{
                  padding: '12px 16px', marginTop: 12, borderRadius: 8,
                  backgroundColor: quarantineResult.quarantined > 0 ? '#0a140a' : '#1a0a0a',
                  border: `1px solid ${quarantineResult.quarantined > 0 ? '#1a3a1a' : '#3a1a1a'}`,
                }}>
                  {quarantineResult.error ? (
                    <span style={{ color: '#f87171', fontSize: 12 }}>Error: {quarantineResult.error}</span>
                  ) : (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#4ade80', marginBottom: 4 }}>
                        ✅ Quarantined {quarantineResult.quarantined} objects ({quarantineResult.quarantined_size_human})
                      </div>
                      <div style={{ fontSize: 11, color: '#525252' }}>
                        Moved to quarantine — review in the Quarantine section below. You can restore or permanently delete from there.
                      </div>
                      {quarantineResult.skipped > 0 && (
                        <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                          ⚠ Skipped {quarantineResult.skipped} (failed re-validation)
                        </div>
                      )}
                      {quarantineResult.errors > 0 && (
                        <div style={{ fontSize: 11, color: '#f87171', marginTop: 2 }}>
                          ✗ Errors: {quarantineResult.errors}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {validation?.error && (
            <div style={{ padding: '10px 14px', borderRadius: 6, backgroundColor: '#1a0a0a', border: '1px solid #3a1a1a', color: '#f87171', fontSize: 12 }}>
              Error: {validation.error}
            </div>
          )}
        </div>
      )}

      {/* ── Orphan Triage Section ── */}
      {report && !report.error && report.s3_orphans_total > 0 && (
        <TriagePanel
          headers={headers}
          onRefresh={() => { runReconciliation(); setQuarantineRefreshKey(k => k + 1); }}
          envFilter={envFilter}
        />
      )}

      {/* ── Quarantine Section ── */}
      <QuarantinePanel
        key={quarantineRefreshKey}
        headers={headers}
        onRefresh={runReconciliation}
        envFilter={envFilter}
      />

      {/* Quarantine confirmation modal */}
      {showQuarantineModal && (
        <ConfirmModal
          title={`Quarantine ${selectedKeys.size} Objects`}
          description={
            <div>
              <p>This will move <strong style={{ color: '#fbbf24' }}>{selectedKeys.size} orphaned S3 objects</strong> ({humanSize(selectedSize)}) to quarantine storage.</p>
              <div style={{
                margin: '10px 0', padding: '8px 12px', borderRadius: 6,
                backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
                fontSize: 11, color: '#4ade80',
              }}>
                <strong>✓ Safety:</strong> Each object will be <strong>re-validated</strong> at quarantine time (8-layer check).
                Objects that fail re-validation will be <strong>skipped</strong>, not quarantined.
              </div>
              <p style={{ marginTop: 8 }}>
                <strong style={{ color: '#4ade80' }}>This is reversible</strong> — you can restore objects from the Quarantine section at any time.
              </p>
              <p style={{ fontSize: 11, color: '#525252', marginTop: 8 }}>
                Quarantined objects are not accessible by the app. If backgrounds break after quarantining, restore them immediately.
              </p>
            </div>
          }
          confirmLabel={`Quarantine ${selectedKeys.size} objects`}
          confirmColor="#f97316"
          onConfirm={executeQuarantine}
          onCancel={() => setShowQuarantineModal(false)}
        />
      )}
      {prodWarning && (
        <ProdWarningModal
          action={prodWarning.action}
          prodCount={prodWarning.prodCount}
          onConfirm={() => { setProdWarning(null); prodWarning.onConfirm(); }}
          onCancel={() => setProdWarning(null)}
        />
      )}
    </AdminLayout>
  );
}

/* ─────────────────────── Helpers ─────────────────────── */

function humanSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
}

/* ─────────────────────── Styles ─────────────────────── */

const s = {
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#141414', color: '#737373', cursor: 'pointer', fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  actionBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '7px 16px', borderRadius: 6, border: '1px solid #2d5a2d',
    backgroundColor: '#0a1a0a', color: '#4ade80', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 6, border: '1px solid #5a2d2d',
    backgroundColor: '#1a0a0a', color: '#f87171', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  quarantineBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 6, border: '1px solid #5a3a1a',
    backgroundColor: '#1a0a04', color: '#f97316', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  cancelBtn: {
    padding: '7px 16px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', color: '#737373', cursor: 'pointer',
    fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif',
  },
  toggleBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 4, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', color: '#525252', cursor: 'pointer',
    fontSize: 10, fontFamily: 'Inter, system-ui, sans-serif',
  },
};
